<template>
    <nav
        v-show="!($route?.meta?.hideBreadcrumb) && routes"
        :class="$style.breadcrumbs"
        class="breadcrumb has-succeeds-separator mb-0"
        aria-label="breadcrumbs">
        <ul>
            <li v-for="(route, i) in routes" :key="i" :class="[(i === routes.length - 1) ? 'is-active' : '', (i === routes.length - 2 && isScreeningRoute(route)) ? $style.dropdownContainer : '']">
                <!-- Projects route has special handling -->
                <router-link v-if="route === 'projects'" :to="{name: 'projects.overview.summary'}" :class="$style.capitalize">{{ getRouteLabel(route || '') }}</router-link>
                
                <!-- Second-last item with dropdown for screening routes -->
                <div v-else-if="i === routes.length - 2 && isScreeningRoute(route)" 
                     :class="[$style.dropdownWrapper, {'is-active': activeDropdownIndex === i}]"
                     :ref="'dropdownWrapper' + i">
                    <div :class="$style.dropdownTriggerWrapper" @mouseenter="toggleDropdown(i, true)">
                        <a href="#" @click.prevent="toggleDropdown(i, !activeDropdownIndex)" :class="[$style.capitalize, $style.dropdownTrigger]">
                            {{ getRouteLabel(route || '') }}
                        </a>
                    </div>
                    <div v-show="activeDropdownIndex === i" 
                         :class="$style.dropdownMenu" 
                         role="menu"
                         @mouseenter="toggleDropdown(i, true)"
                         @mouseleave="toggleDropdown(i, false)">
                        <div :class="$style.dropdownContent">
                            <router-link 
                                v-for="option in alternativeScreeningRoutes" 
                                :key="option.name"
                                :to="option.to" 
                                :class="[$style.dropdownItem, $style.capitalize]">
                                {{ getRouteLabel(option.label) }}
                            </router-link>
                        </div>
                    </div>
                </div>
                
                <!-- Regular breadcrumb items -->
                <router-link v-else :to="{ name: routes.slice(0, i + 1).join('.') }" :class="$style.capitalize">{{ 
                    getRouteLabel(route || '') }}</router-link>
            </li>
        </ul>
    </nav>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';

interface ScreeningRouteOption {
    name: string;
    to: { name: string };
    label: string;
}

@Component
export default class Breadcrumb extends Vue {
    // Add refs typing
    $refs!: {
        [key: string]: HTMLElement | HTMLElement[] | undefined;
    };
    // Track which dropdown is currently active
    activeDropdownIndex: number | null = null;
    
    // Toggle dropdown visibility
    toggleDropdown(index: number, isVisible: boolean): void {
        this.activeDropdownIndex = isVisible ? index : null;
    }

    // Handle click outside to close dropdown
    handleClickOutside(event: MouseEvent) {
        if (this.activeDropdownIndex === null) return;
        const refName = 'dropdownWrapper' + this.activeDropdownIndex;
        let dropdownEl = this.$refs[refName];
        // Vue 2: $refs with v-for is array if multiple, or undefined if not rendered
        if (Array.isArray(dropdownEl)) {
            dropdownEl = dropdownEl[0];
        }
        if (dropdownEl && typeof dropdownEl.contains === 'function' && !dropdownEl.contains(event.target as Node)) {
            this.activeDropdownIndex = null;
        }
    }
    mounted() {
        document.addEventListener('click', this.handleClickOutside);
    }
    beforeDestroy() {
        document.removeEventListener('click', this.handleClickOutside);
    }

    get routes(): string[] | undefined {
        return this.$route.name?.split('.');
    }

    get currentRouteName(): string {
        return this.$route.name || '';
    }

    get alternativeScreeningRoutes(): ScreeningRouteOption[] {
        // If we're in title-and-abstract-screening, show full-text-screening option
        if (this.isInTitleAndAbstractScreening) {
            return [{
                name: 'full-text-screening',
                to: { name: `projects.screening.full-text-screening.awaiting-screening` },
                label: 'full text screening'
            }];
        }
        
        // If we're in full-text-screening, show title-and-abstract-screening option
        if (this.isInFullTextScreening) {
            return [{
                name: 'title-and-abstract-screening',
                to: { name: `projects.screening.title-and-abstract-screening.awaiting-screening` },
                label: 'title and abstract screening'
            }];
        }
        
        return [];
    }

    get isInTitleAndAbstractScreening(): boolean {
        return this.currentRouteName.includes('title-and-abstract-screening');
    }

    get isInFullTextScreening(): boolean {
        return this.currentRouteName.includes('full-text-screening');
    }

    getRouteLabel(routeName: string): string {
        if (routeName.length) {
            return routeName.replaceAll('-', ' ');
        }
        return '';
    }

    isScreeningRoute(route: string): boolean {
        return route === 'title-and-abstract-screening' || route === 'full-text-screening';
    }
}
</script>

<style lang="scss" module>
.breadcrumbs {
    margin: 0 6vw;
    padding-top: 2.5vh;
    padding-bottom: 1.5vh;

    .capitalize {
        text-transform: capitalize;
    }
}

.dropdownContainer {
    position: relative;
}

.dropdownWrapper {
    display: inline-block;
    position: relative;
}

.dropdownTriggerWrapper {
    display: inline-block;
}

.dropdownTrigger {
    display: inline-flex;
    align-items: center;
    
    &:hover {
        color: $breadcrumb-item-color;
    }
}

.dropdownMenu {
    position: absolute;
    left: 0;
    top: 100%;
    z-index: 20;
    margin-top: 4px;
    padding-top: 4px; /* Add padding to create a hover-friendly gap */
}

.dropdownContent {
    display: flex;
    flex-direction: column;
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 0;
}

.dropdownItem {
    color: $breadcrumb-item-color;
    padding: 0.375rem 0;
    text-decoration: none;
    white-space: nowrap;
    margin-bottom: 4px; /* Add space between items for better usability */
    
    &:hover {
        text-decoration: underline;
    }
}
</style>
